import { useEffect } from 'react';
import './SurfAnimation.css';

/**
 * PROFESSIONAL SURF ANIMATION
 * Water-type special move with massive tidal wave effects
 * 
 * Key Features:
 * - Water-type special attack (90 base power, 100% accuracy)
 * - Hits all adjacent opponents in double battles
 * - Massive tidal wave with realistic water physics
 * - Professional gaming company quality with oceanic timing
 * - Special move animation with area-of-effect mechanics
 */
export default function SurfAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate wave trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Oceanic Summoning (0-600ms)
    console.log('Surf Phase 1: Oceanic Summoning');
    
    // Player enhancement with oceanic aura
    playerEl.classList.add('surf-user-charge');
    
    // Oceanic summoning aura around player
    const oceanicAura = document.createElement('div');
    oceanicAura.className = 'surf-oceanic-aura';
    oceanicAura.style.left = `${playerX - 100}px`;
    oceanicAura.style.top = `${playerY - 100}px`;
    container.appendChild(oceanicAura);
    elements.push(oceanicAura);

    // Oceanic energy particles around player
    for (let i = 0; i < 20; i++) {
      const energyParticle = document.createElement('div');
      energyParticle.className = 'surf-energy-particle';
      const angle = (i / 20) * 360;
      const radius = 60 + (i % 4) * 15;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      energyParticle.style.left = `${particleX - 4}px`;
      energyParticle.style.top = `${particleY - 4}px`;
      energyParticle.style.animationDelay = `${i * 25}ms`;
      energyParticle.textContent = '💧';
      container.appendChild(energyParticle);
      elements.push(energyParticle);
    }

    // Water gathering effects around player
    for (let i = 0; i < 15; i++) {
      const waterGather = document.createElement('div');
      waterGather.className = 'surf-water-gather';
      const gatherAngle = (i / 15) * 360;
      const gatherRadius = 80;
      const gatherX = playerX + Math.cos(gatherAngle * Math.PI / 180) * gatherRadius;
      const gatherY = playerY + Math.sin(gatherAngle * Math.PI / 180) * gatherRadius;
      waterGather.style.left = `${gatherX - 6}px`;
      waterGather.style.top = `${gatherY - 6}px`;
      waterGather.style.animationDelay = `${i * 35}ms`;
      container.appendChild(waterGather);
      elements.push(waterGather);
    }

    // PHASE 2: Massive Tidal Wave Formation (600-1200ms)
    const phase2Timer = setTimeout(() => {
      console.log('Surf Phase 2: Massive Tidal Wave Formation');
      
      // Create the massive tidal wave
      const tidalWave = document.createElement('div');
      tidalWave.className = 'surf-tidal-wave';
      tidalWave.style.left = `${playerX - 150}px`;
      tidalWave.style.top = `${playerY - 75}px`;
      tidalWave.style.setProperty('--target-x', `${opponentX}px`);
      tidalWave.style.setProperty('--target-y', `${opponentY}px`);
      tidalWave.style.setProperty('--distance', `${distance}px`);
      container.appendChild(tidalWave);
      elements.push(tidalWave);
      
      // Wave crest foam effects
      for (let i = 0; i < 12; i++) {
        const crestFoam = document.createElement('div');
        crestFoam.className = 'surf-crest-foam';
        crestFoam.style.left = `${playerX - 120 + i * 20}px`;
        crestFoam.style.top = `${playerY - 60}px`;
        crestFoam.style.animationDelay = `${i * 40}ms`;
        container.appendChild(crestFoam);
        elements.push(crestFoam);
      }
    }, 600);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Tidal Wave Impact (1200-1800ms)
    const phase3Timer = setTimeout(() => {
      console.log('Surf Phase 3: Tidal Wave Impact');
      
      // Opponent hit reaction
      opponentEl.classList.add('surf-opponent-hit');
      
      // Massive water impact explosion
      const waterImpact = document.createElement('div');
      waterImpact.className = 'surf-water-impact';
      waterImpact.style.left = `${opponentX - 120}px`;
      waterImpact.style.top = `${opponentY - 120}px`;
      container.appendChild(waterImpact);
      elements.push(waterImpact);
      
      // Water splash particles
      for (let i = 0; i < 25; i++) {
        const splashParticle = document.createElement('div');
        splashParticle.className = 'surf-splash-particle';
        const splashAngle = (i / 25) * 360;
        const splashRadius = 80 + Math.random() * 60;
        const splashX = opponentX + Math.cos(splashAngle * Math.PI / 180) * splashRadius;
        const splashY = opponentY + Math.sin(splashAngle * Math.PI / 180) * splashRadius;
        splashParticle.style.left = `${splashX - 5}px`;
        splashParticle.style.top = `${splashY - 5}px`;
        splashParticle.style.animationDelay = `${i * 15}ms`;
        splashParticle.style.setProperty('--scatter-x', `${Math.cos(splashAngle * Math.PI / 180) * 40}px`);
        splashParticle.style.setProperty('--scatter-y', `${Math.sin(splashAngle * Math.PI / 180) * 40}px`);
        container.appendChild(splashParticle);
        elements.push(splashParticle);
      }
      
      // Water shockwave rings
      for (let i = 0; i < 4; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'surf-shockwave';
          shockwave.style.left = `${opponentX - 80}px`;
          shockwave.style.top = `${opponentY - 80}px`;
          shockwave.style.animationDelay = `${i * 100}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 120);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }
    }, 1200);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Oceanic Aftermath (1800-2200ms)
    const phase4Timer = setTimeout(() => {
      console.log('Surf Phase 4: Oceanic Aftermath');
      
      // Remove charge effects
      playerEl.classList.remove('surf-user-charge');
      
      // Oceanic mist effects
      for (let i = 0; i < 12; i++) {
        const mist = document.createElement('div');
        mist.className = 'surf-oceanic-mist';
        const mistX = opponentX + (Math.random() - 0.5) * 200;
        const mistY = opponentY + (Math.random() - 0.5) * 100;
        mist.style.left = `${mistX - 15}px`;
        mist.style.top = `${mistY - 15}px`;
        mist.style.animationDelay = `${i * 30}ms`;
        container.appendChild(mist);
        elements.push(mist);
      }
      
      // Water pooling effect
      const waterPool = document.createElement('div');
      waterPool.className = 'surf-water-pool';
      waterPool.style.left = `${opponentX - 100}px`;
      waterPool.style.top = `${opponentY + 30}px`;
      container.appendChild(waterPool);
      elements.push(waterPool);
    }, 1800);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2200ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Surf: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('surf-user-charge');
      opponentEl.classList.remove('surf-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 2200);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('surf-user-charge');
      opponentEl.classList.remove('surf-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

SurfAnimation.moveType = 'water';
